import React from "react";
import { Link } from "react-router-dom";
import { ASSETS, LINKS, ARC, CA } from "@/config";
import { useLanguage } from "@/i18n/LanguageContext";
import { XLogo } from "@/components/Navbar";
import { shortAddress } from "@/components/Wallet";

// Footer styled as a system shutdown interface.
export default function Footer() {
  const { t } = useLanguage();

  return (
    <footer
      data-testid="footer"
      className="relative border-t border-mizu-cyan/15 bg-[#020611]"
    >
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        <div className="grid gap-10 md:grid-cols-3">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3">
              <img
                src={ASSETS.logo}
                alt="MIZUKARA"
                className="h-10 w-10 rounded-full"
              />
              <div>
                <div className="font-display text-sm font-bold tracking-[0.2em] text-white">
                  MIZUKARA
                </div>
                <div className="font-jp text-[10px] tracking-[0.4em] text-mizu-cyan/80">
                  ミズカラ
                </div>
              </div>
            </div>
            <p className="mt-4 font-mono text-[11px] tracking-[0.15em] text-mizu-dim">
              {t("footer.designation")}
            </p>
            <a
              href={LINKS.x}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-x-link"
              className="mt-4 inline-flex items-center gap-2 border border-mizu-cyan/25 px-3 py-1.5 font-mono text-[10px] tracking-[0.2em] text-mizu-dim transition-colors hover:border-mizu-cyan/60 hover:text-mizu-cyan"
            >
              <XLogo size={12} /> X
            </a>
          </div>

          {/* System readout */}
          <div className="font-mono text-[11px] leading-6 text-mizu-dim">
            <div className="mb-2 text-[10px] tracking-[0.3em] text-mizu-faint">
              SYSTEM
            </div>
            <div className="flex justify-between border-b border-mizu-cyan/10 py-1">
              <span>{t("footer.network")}</span>
              <span className="text-mizu-silver">{ARC.name}</span>
            </div>
            <div className="flex justify-between border-b border-mizu-cyan/10 py-1">
              <span>{t("footer.status")}</span>
              <span className="flex items-center gap-1.5 text-mizu-cyan">
                <span className="h-1.5 w-1.5 animate-pulse-glow bg-mizu-cyan" />
                {t("footer.online")}
              </span>
            </div>
            <div className="flex justify-between border-b border-mizu-cyan/10 py-1">
              <span>{t("footer.ca")}</span>
              <a
                href={`${LINKS.explorer}/address/${CA}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-ca-link"
                className="text-mizu-cyan/80 transition-colors hover:text-mizu-cyan"
              >
                {shortAddress(CA)}
              </a>
            </div>
          </div>

          {/* External links */}
          <div className="font-mono text-[11px]">
            <div className="mb-2 text-[10px] tracking-[0.3em] text-mizu-faint">
              EXTERNAL
            </div>
            <div className="flex flex-col gap-2">
              <a
                href={LINKS.buy}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-argus-link"
                className="text-mizu-dim transition-colors hover:text-mizu-cyan"
              >
                ARGUS ↗
              </a>
              <a
                href={LINKS.chart}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-dexscreener-link"
                className="text-mizu-dim transition-colors hover:text-mizu-cyan"
              >
                DEXSCREENER ↗
              </a>
              <a
                href={LINKS.explorer}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-explorer-link"
                className="text-mizu-dim transition-colors hover:text-mizu-cyan"
              >
                ARC-SCAN ↗
              </a>
              <Link
                to="/connect"
                data-testid="footer-connect-link"
                className="text-mizu-dim transition-colors hover:text-mizu-cyan"
              >
                CONNECT →
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-12 border-t border-mizu-cyan/10 pt-6">
          <p className="font-mono text-xs text-mizu-cyan/70 mizu-glow" data-testid="footer-end">
            {t("footer.end")}
            <span className="animate-blink">_</span>
          </p>
          <div className="mt-3 flex flex-col justify-between gap-2 sm:flex-row">
            <p className="font-mono text-[10px] tracking-[0.2em] text-mizu-faint">
              {t("footer.rights")}
            </p>
            <p className="font-mono text-[10px] text-mizu-faint/70">
              {t("footer.note")}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
